import { Box } from '@deckee/deck-hand';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUnitsConfig } from '../../../../../services/store/modules/app-controls';
import { SwellChart } from '../charts';
import {
  enrichedSeriesToTimeSeries,
  formatSwellHeightDisplay,
  formatSwellPeriodSecondaryDisplay,
  getClosestEnrichedPoint,
  getForecastDayDomain,
  graphPointsToEnrichedSeries,
  type WeatherForecastGraphPoint,
} from '../../helpers/graph/graphData';
import { useGraphTime } from './GraphTimeContext';
import CollapsibleCard from '../CollapsibleCard/CollapsibleCard';
import GraphCardHeader from './GraphCardHeader';
import GraphCardNoData from './GraphCardNoData';

export interface SwellCardProps {
  swellPoints: WeatherForecastGraphPoint[];
  swellPeriodPoints: WeatherForecastGraphPoint[];
  selectedDate: string;
  timeZone: string;
  isToday: boolean;
  latitude: number;
  longitude: number;
  locationName?: string;
}

const SwellCard: FC<SwellCardProps> = ({
  swellPoints,
  swellPeriodPoints,
  selectedDate,
  timeZone,
  isToday,
  latitude,
  longitude,
  locationName,
}) => {
  const unitsConfig = useSelector(selectUnitsConfig);

  const {
    selectedChartTime,
    defaultChartTime,
    handleSelectedChartTimeChange,
    handleChartScrubStart,
    handleChartScrubEnd,
  } = useGraphTime('swell');

  const swellSeries = useMemo(
    () => graphPointsToEnrichedSeries(swellPoints, selectedDate, timeZone),
    [selectedDate, swellPoints, timeZone],
  );

  const swellPeriodSeries = useMemo(
    () =>
      graphPointsToEnrichedSeries(swellPeriodPoints, selectedDate, timeZone),
    [selectedDate, swellPeriodPoints, timeZone],
  );

  const domain = useMemo(
    () => getForecastDayDomain(selectedDate, timeZone),
    [selectedDate, timeZone],
  );

  const chartData = useMemo(
    () => enrichedSeriesToTimeSeries(swellSeries, domain),
    [domain, swellSeries],
  );

  const selectedSwellPoint = useMemo(() => {
    if (!selectedChartTime) {
      return undefined;
    }

    return getClosestEnrichedPoint(swellSeries, selectedChartTime);
  }, [selectedChartTime, swellSeries]);

  const selectedSwellPeriodPoint = useMemo(() => {
    if (!selectedChartTime) {
      return undefined;
    }

    return getClosestEnrichedPoint(swellPeriodSeries, selectedChartTime);
  }, [selectedChartTime, swellPeriodSeries]);

  const primaryDisplay = selectedSwellPoint
    ? formatSwellHeightDisplay(
        selectedSwellPoint.value,
        unitsConfig,
        selectedSwellPoint.directionText,
      )
    : '—';

  const secondaryDisplay = selectedSwellPeriodPoint
    ? formatSwellPeriodSecondaryDisplay(
        selectedSwellPeriodPoint.value,
        unitsConfig,
      )
    : null;

  if (chartData.length === 0) {
    return (
      <CollapsibleCard
        title="Swell"
        testId="weather-forecast-swell-card"
        analyticsCard="swell"
      >
        <GraphCardNoData
          chartLabel="Swell"
          stationType="swell"
          isLocationMissingData={swellPoints.length === 0}
          latitude={latitude}
          longitude={longitude}
          selectedDate={selectedDate}
          locationName={locationName}
        />
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      title="Swell"
      testId="weather-forecast-swell-card"
      analyticsCard="swell"
    >
      <GraphCardHeader primary={primaryDisplay} secondary={secondaryDisplay} />

      <Box width="100%">
        <SwellChart
          data={chartData}
          domain={domain}
          timeZone={timeZone}
          directionSeries={swellSeries}
          periodSeries={swellPeriodSeries}
          selectedTime={selectedChartTime}
          defaultSelectedTime={defaultChartTime}
          onSelectedTimeChange={handleSelectedChartTimeChange}
          onScrubStart={handleChartScrubStart}
          onScrubEnd={handleChartScrubEnd}
        />
      </Box>
    </CollapsibleCard>
  );
};

export default SwellCard;
